import React from 'react';

import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { type N, type Point, compact, fmt, pct } from './fidelityModel';

export const lightSeries = ['#0F464F', '#35817E', '#4EFEB9', '#658E98', '#A5BDB0', '#21494A', '#77AA99'];
export const darkSeries = ['#4EFEB9', '#8ECAC0', '#C4DDD1', '#5CB8B0', '#A8B8A3', '#E3E7CF', '#70A5AD'];
export function seriesColour(index: number, dark: boolean) {
  return (dark ? darkSeries : lightSeries)[index % 7];
}
const dash = ['', '6 3', '2 3', '10 3 2 3'];
const dateLabel = (v: number) =>
  new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const exactDate = (v: number) => new Date(v).toISOString().slice(0, 10);
const valueLabel = (n: N, percentage: boolean) => (percentage ? pct(n, 2) : fmt(n));

export function ComparisonPlot({
  points,
  yoy = false,
  conversion = false,
  dark = false,
  chartId = 'chart',
  end,
}: {
  points: Array<Point>;
  yoy?: boolean;
  conversion?: boolean;
  dark?: boolean;
  chartId?: string;
  end: string;
}) {
  const data = yoy ? points.filter((p) => p.date <= end) : points;
  const vals = data.map((p) => p.yoy).filter((n): n is number => n !== null);
  const low = Math.min(0, ...vals),
    high = Math.max(0, ...vals);
  const lower = Math.floor(low * 10) / 10;
  const upper = Math.max(Math.ceil(high * 10) / 10, lower + 0.1);
  const domain = [lower, upper];
  const threshold = upper / (upper - lower);
  const current = dark ? '#4EFEB9' : '#0F464F',
    previous = dark ? '#97BAB5' : '#7C9C99',
    negative = dark ? '#F9F7F1' : '#0F0C08';
  const percentage = yoy || conversion;
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      <LineChart accessibilityLayer data={data} margin={{ left: 1, right: 18, top: 12, bottom: 25 }}>
        <defs>
          <linearGradient id={chartId} x1="0" y1="0" x2="0" y2="1">
            <stop offset={threshold} stopColor={current} />
            <stop offset={threshold} stopColor={negative} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={dark ? '#32484A' : '#DCE5E0'} strokeDasharray="2 3" />
        <XAxis
          dataKey="at"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={dateLabel}
          tick={{ fontSize: 10, fill: dark ? '#C5D4CF' : '#526961' }}
          minTickGap={35}
          tickCount={5}
          axisLine={false}
          tickLine={false}
          label={{
            value: 'Sydney business date',
            position: 'insideBottom',
            offset: -18,
            fontSize: 10,
            fill: dark ? '#C5D4CF' : '#526961',
          }}
        />
        <YAxis
          width={58}
          domain={yoy ? domain : conversion ? [0, 'auto'] : ['auto', 'auto']}
          tickFormatter={(v) => (percentage ? pct(v, conversion ? 2 : 0) : compact(v))}
          tick={{ fontSize: 10, fill: dark ? '#C5D4CF' : '#526961' }}
          axisLine={false}
          tickLine={false}
          tickCount={4}
        />
        <ReportChartTooltip
          isAnimationActive={false}
          cursor={{ stroke: dark ? '#4EFEB9' : '#0F464F', strokeDasharray: '3 3' }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) {
              return null;
            }
            const p = payload[0].payload as Point;
            return (
              <div
                style={{
                  background: dark ? '#14292B' : '#FFFFFF',
                  color: dark ? '#F9F7F1' : '#0F0C08',
                  border: `1px solid ${current}`,
                  borderRadius: 5,
                  padding: '9px 12px',
                  fontSize: 12,
                  boxShadow: '0 2px 8px #0002',
                }}
              >
                <strong>{exactDate(Number(label))}</strong>
                {yoy ? (
                  <p>YoY: {pct(p.yoy, 2)}</p>
                ) : (
                  <>
                    <p>
                      {conversion ? 'CR' : 'Current moving average'}: {valueLabel(p.current, percentage)}
                    </p>
                    {!conversion && <p>Prior year moving average: {valueLabel(p.previous, false)}</p>}
                  </>
                )}
                {conversion ? (
                  <p>
                    {fmt(p.packages ?? null)} packages / {fmt(p.views ?? null)} views
                  </p>
                ) : (
                  <p>
                    Daily current / LY: {fmt(p.raw)} / {fmt(p.rawLY)}
                  </p>
                )}
              </div>
            );
          }}
        />
        {yoy && <ReferenceLine y={0} stroke={negative} strokeDasharray="4 3" />}
        <Line
          type="linear"
          dataKey={yoy ? 'yoy' : 'current'}
          name={yoy ? 'YoY' : conversion ? 'CR' : 'Current'}
          stroke={yoy ? `url(#${chartId})` : current}
          strokeWidth={2.1}
          dot={data.length < 3}
          activeDot={{ r: 4 }}
          connectNulls={false}
          isAnimationActive={false}
        />
        {!yoy && !conversion && (
          <Line
            type="linear"
            dataKey="previous"
            name="Prior year"
            stroke={previous}
            strokeWidth={1.6}
            strokeDasharray="6 3"
            dot={false}
            activeDot={{ r: 3 }}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

export function SharePlot({
  data,
  names,
  dark = false,
  highlight = 'All',
  logarithmic = false,
}: {
  data: Array<Record<string, string | number | null>>;
  names: Array<string>;
  dark?: boolean;
  highlight?: string;
  logarithmic?: boolean;
}) {
  return (
    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
      <LineChart
        accessibilityLayer
        data={
          logarithmic
            ? data.map((row) =>
                Object.fromEntries(
                  Object.entries(row).map(([key, value]) => [
                    key,
                    names.includes(key) && typeof value === 'number' && value <= 0 ? null : value,
                  ]),
                ),
              )
            : data
        }
        margin={{ left: 1, right: 18, top: 12, bottom: 25 }}
      >
        <CartesianGrid vertical={false} stroke={dark ? '#32484A' : '#DCE5E0'} strokeDasharray="2 3" />
        <XAxis
          dataKey="at"
          type="number"
          domain={['dataMin', 'dataMax']}
          tickFormatter={dateLabel}
          tick={{ fontSize: 10, fill: dark ? '#C5D4CF' : '#526961' }}
          minTickGap={40}
          axisLine={false}
          tickLine={false}
          label={{
            value: 'Sydney business date',
            position: 'insideBottom',
            offset: -18,
            fontSize: 10,
            fill: dark ? '#C5D4CF' : '#526961',
          }}
        />
        <YAxis
          width={58}
          scale={logarithmic ? 'log' : 'auto'}
          domain={
            logarithmic
              ? ['auto', 'auto']
              : [(min: number) => Math.min(0, min) * 1.04, (max: number) => Math.max(0.05, max) * 1.04]
          }
          tickFormatter={(v) => pct(v, 0)}
          tick={{ fontSize: 10, fill: dark ? '#C5D4CF' : '#526961' }}
          axisLine={false}
          tickLine={false}
        />
        <ReportChartTooltip
          isAnimationActive={false}
          content={({ active, payload, label }) =>
            active && payload?.length ? (
              <div
                style={{
                  background: dark ? '#14292B' : '#FFFFFF',
                  color: dark ? '#F9F7F1' : '#0F0C08',
                  border: '1px solid #0F464F',
                  padding: 10,
                  fontSize: 11,
                }}
              >
                <strong>{exactDate(Number(label))}</strong>
                {[...payload]
                  .filter((p) => highlight === 'All' || p.name === highlight)
                  .sort((a, b) => Number(b.value) - Number(a.value))
                  .slice(0, 10)
                  .map((p) => (
                    <p key={String(p.name)}>
                      {p.name}: {pct(p.value as number, 2)}
                    </p>
                  ))}
                {highlight === 'All' && payload.length > 10 && (
                  <p>Top 10 shares shown. Select a legend entry to inspect another.</p>
                )}
              </div>
            ) : null
          }
        />
        {names.map((name, i) => (
          <Line
            key={name}
            type="linear"
            dataKey={name}
            name={name}
            stroke={seriesColour(i, dark)}
            strokeDasharray={dash[Math.floor(i / 7) % dash.length]}
            strokeOpacity={highlight === 'All' || highlight === name ? 1 : 0.12}
            strokeWidth={highlight === name ? 2.8 : 1.7}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}
